// src/pages/WrapDetailPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import logo from '../assets/logo.jpeg';

const slugify = (s = '') =>
  s.toString().trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

export default function WrapDetailPage() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const location = useLocation();
  const stateItem = location.state?.item;

  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(!stateItem);
  const [error, setError] = useState('');

  // Robust fetch (supports top-level array or { data: [...] })
  useEffect(() => {
    let mounted = true;
    if (stateItem) return;
    (async () => {
      try {
        const tryUrls = [
          '/wraps.json',
          `${(process.env.PUBLIC_URL || '').replace(/\/$/, '')}/wraps.json`,
        ].filter((u, i, a) => a.indexOf(u) === i);

        let loaded = null;
        for (const url of tryUrls) {
          try {
            const res = await fetch(url, { cache: 'no-store' });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json = await res.json();
            const arr = Array.isArray(json) ? json : Array.isArray(json?.data) ? json.data : null;
            if (!arr) throw new Error('JSON not an array');
            loaded = arr;
            break;
          } catch (_) {}
        }
        if (!mounted) return;
        if (!loaded) throw new Error('Failed to load wraps.json from known locations');
        setAll(loaded);
      } catch (e) {
        if (!mounted) return;
        setError(e?.message || 'Failed to load wrap data.');
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [stateItem]);

  const item = useMemo(() => {
    if (stateItem) return stateItem;
    return all.find((w, idx) => slugify(w.name || `wrap-${idx}`) === slug);
  }, [stateItem, all, slug]);

  const title = item?.name || 'Wrap Details';

  // Fields shown as compact “pills”
  const quickFacts = [
    ['brand', 'Brand'],
    ['variant', 'Variant'],
    ['materials', 'Materials'],
    ['release_type', 'Release Type'],
    ['release_date', 'Release Date'],
  ];

  const hiddenKeys = new Set(['name', 'image', 'description', 'notes', 'source', '_from']);

  // Image loading & error states
  const [imgLoading, setImgLoading] = useState(true);
  const [imgError, setImgError] = useState(false);

  // Entrance animation
  const [animatedIn, setAnimatedIn] = useState(false);
  useEffect(() => {
    if (!loading && !error) {
      const t = setTimeout(() => setAnimatedIn(true), 0);
      return () => clearTimeout(t);
    }
  }, [loading, error]);

  return (
    <div id="wrap-detail-page" style={pageWrap}>
      {/* App bar (logo LEFT) */}
      <div style={appBar}>
        <div style={leftBarGroup}>
          <img
            src={logo}
            alt="WrapMarket"
            style={brandLogo}
            onClick={() => navigate('/dashboard')}
          />
          <button
            onClick={() => navigate(-1)}
            style={iconBtn}
            aria-label="Back"
            title="Back"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M15 18l-6-6 6-6" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <div style={crumbs}>
            <span style={crumbLink} onClick={() => navigate('/dashboard')}>Wraps</span>
            <span style={crumbSep}>/</span>
            <span style={crumbCurrent}>{title}</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div style={hero}>
        <h1 style={heroTitle}>{title}</h1>
        {item?.brand && (
          <div style={heroSub}>
            <span style={brandBadge}>{item.brand}</span>
            {item?.variant && <span style={chip}>{item.variant}</span>}
            {item?.release_type && <span style={chipMuted}>{item.release_type}</span>}
            {item?.release_date && <span style={chipMuted}>{item.release_date}</span>}
          </div>
        )}
      </div>

      <div style={container}>
        {loading ? (
          <div style={card}>
            <div style={{ ...shimmerBlock, height: 280, borderRadius: 12 }} />
            <div style={{ marginTop: 16 }}>
              <div style={{ ...shimmerLine, width: '60%' }} />
              <div style={{ ...shimmerLine, width: '40%', marginTop: 8 }} />
              <div style={{ ...shimmerLine, width: '90%', marginTop: 16 }} />
              <div style={{ ...shimmerLine, width: '80%', marginTop: 6 }} />
              <div style={{ ...shimmerLine, width: '75%', marginTop: 6 }} />
            </div>
          </div>
        ) : error ? (
          <div style={emptyWrap}>
            <div style={emptyIcon}>⚠️</div>
            <div style={emptyTitle}>Something went wrong</div>
            <div style={emptyText}>{error}</div>
            <button style={primaryBtn} onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
          </div>
        ) : !item ? (
          <div style={emptyWrap}>
            <div style={emptyIcon}>🧐</div>
            <div style={emptyTitle}>Wrap not found</div>
            <div style={emptyText}>We couldn’t find a wrap matching this link.</div>
            <button style={primaryBtn} onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
          </div>
        ) : (
          <div
            style={{ ...grid, opacity: animatedIn ? 1 : 0, transform: animatedIn ? 'translateY(0)' : 'translateY(8px)' }}
            className="grid-responsive"
          >
            {/* Media */}
            <div style={card}>
              {item.image && !imgError ? (
                <div style={imageWrap}>
                  {imgLoading && (
                    <div style={imageLoader}>
                      <svg width="28" height="28" viewBox="0 0 50 50" aria-hidden>
                        <circle cx="25" cy="25" r="20" stroke="#9ca3af" strokeWidth="4" fill="none" strokeDasharray="31.4 31.4">
                          <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite" />
                        </circle>
                      </svg>
                      <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>Loading image…</div>
                    </div>
                  )}
                  <img
                    src={item.image}
                    alt={item.name || 'Wrap'}
                    style={{ ...heroImage, opacity: imgLoading ? 0 : 1, transition: 'opacity .35s ease' }}
                    onLoad={() => setImgLoading(false)}
                    onError={() => { setImgError(true); setImgLoading(false); }}
                  />
                </div>
              ) : (
                <div style={imagePlaceholder}>No image available</div>
              )}
            </div>

            {/* Details */}
            <div style={card}>
              <div style={section}>
                <div style={sectionTitle}>Details</div>
                <div style={pillStack}>
                  {quickFacts.map(([key, label]) => {
                    const val = item[key];
                    if (!val) return null;
                    return (
                      <div key={key} style={pill}>
                        <span style={pillKey}>{label}</span>
                        <span>{String(val)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {(item.description || item.notes) && (
                <div style={section}>
                  <div style={sectionTitle}>Description</div>
                  <p style={descText}>{item.description || item.notes}</p>
                </div>
              )}

              {/* Any extra fields */}
              <div style={section}>
                {Object.entries(item)
                  .filter(
                    ([k, v]) =>
                      v != null &&
                      v !== '' &&
                      !hiddenKeys.has(k) &&
                      !quickFacts.some(([qk]) => qk === k)
                  )
                  .map(([k, v]) => (
                    <div key={k} style={kvRow}>
                      <div style={kvKey}>{k.replace(/_/g, ' ')}</div>
                      <div style={kvVal}>{String(v)}</div>
                    </div>
                  ))}
              </div>

              {/* Actions */}
              <div style={actionsRow} className="actions-responsive">
                <button onClick={() => navigate(-1)} style={secondaryBtn}>Back</button>
                {item.source && (
                  <a href={item.source} target="_blank" rel="noopener noreferrer" style={primaryBtn}>
                    View Source
                  </a>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ===== Styles (clamped to screen width) ===== */
const pageWrap = {
  minHeight: '100vh',
  background: '#f5f7fb',
  color: '#111827',
  maxWidth: '100vw',
  overflowX: 'hidden',
  boxSizing: 'border-box',
};

const appBar = {
  height: 56,
  width: '100%',
  maxWidth: '100vw',
  background: '#ffffffcc',
  backdropFilter: 'saturate(180%) blur(10px)',
  borderBottom: '1px solid #e5e7eb',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 12,
  padding: '0 16px',
  position: 'sticky',
  top: 0,
  zIndex: 30,
  boxSizing: 'border-box',
};

const leftBarGroup = { display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 };
const iconBtn = { background: 'transparent', border: '1px solid transparent', cursor: 'pointer', padding: 6, borderRadius: 8 };
const crumbs = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#6b7280', minWidth: 0, flexWrap: 'wrap' };
const crumbLink = { cursor: 'pointer', color: '#2563eb', fontWeight: 600, whiteSpace: 'nowrap' };
const crumbSep = { opacity: 0.5 };
const crumbCurrent = { color: '#374151', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

const brandLogo = {
  display: 'block',
  height: 28,
  width: 'auto',
  cursor: 'pointer',
  filter: 'drop-shadow(0 1px 1px rgba(0,0,0,.25))',
};

const hero = {
  padding: '28px 16px 8px',
  background: 'linear-gradient(180deg, rgba(255,255,255,0.75) 0%, rgba(245,247,251,0) 100%)',
  boxSizing: 'border-box',
  maxWidth: '100vw',
};

const heroTitle = { margin: 0, fontSize: 26, fontWeight: 800, letterSpacing: 0.2, color: '#0f172a' };
const heroSub = { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 };

const brandBadge = {
  background: '#eef2ff',
  color: '#4338ca',
  border: '1px solid #e0e7ff',
  borderRadius: 999,
  padding: '6px 10px',
  fontSize: 12,
  fontWeight: 700,
};
const chip = { ...brandBadge, background: '#ecfeff', color: '#155e75', borderColor: '#cffafe' };
const chipMuted = { ...brandBadge, background: '#f3f4f6', color: '#374151', borderColor: '#e5e7eb' };

const container = {
  width: '100%',
  maxWidth: '100vw',          // never exceed screen width
  margin: '0 auto',
  padding: '0 16px 24px',
  boxSizing: 'border-box',
};

const grid = {
  width: '100%',
  display: 'grid',
  gridTemplateColumns: '1.2fr 1fr',
  gap: 16,
  transition: 'opacity .35s ease, transform .35s ease',
  boxSizing: 'border-box',
};

const card = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 14,
  padding: 16,
  boxShadow: '0 8px 30px rgba(0,0,0,.06)',
  boxSizing: 'border-box',
  overflow: 'hidden',
};

const imageWrap = { position: 'relative', minHeight: 320 };
const imageLoader = {
  position: 'absolute',
  inset: 0,
  display: 'grid',
  placeItems: 'center',
  background: 'rgba(249,250,251,.6)',
  borderRadius: 10,
  backdropFilter: 'blur(2px)',
};

const heroImage = {
  width: '100%',
  height: '100%',
  maxHeight: 520,
  objectFit: 'contain',
  borderRadius: 10,
  maxWidth: '100%', // ensures no overflow
};

const imagePlaceholder = {
  height: 320,
  display: 'grid',
  placeItems: 'center',
  color: '#9ca3af',
  background: 'repeating-linear-gradient(45deg,#f9fafb,#f9fafb 10px,#f3f4f6 10px,#f3f4f6 20px)',
  borderRadius: 12,
  width: '100%',
  boxSizing: 'border-box',
};

const section = { marginBottom: 18 };
const sectionTitle = { fontSize: 14, fontWeight: 800, color: '#111827', letterSpacing: 0.4, marginBottom: 10, textTransform: 'uppercase' };

const pillStack = { display: 'grid', gridTemplateColumns: '1fr', gap: 8 };
const pill = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  background: '#f9fafb',
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  padding: '8px 10px',
  fontSize: 13,
  color: '#374151',
};
const pillKey = { fontWeight: 700, color: '#111827', minWidth: 120 };

const descText = { margin: 0, fontSize: 14, lineHeight: 1.65, color: '#334155', whiteSpace: 'pre-wrap' };

const kvRow = { display: 'grid', gridTemplateColumns: '160px 1fr', gap: 10, padding: '8px 0', borderTop: '1px dashed #eef2f7', boxSizing: 'border-box' };
const kvKey = { fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'capitalize' };
const kvVal = { fontSize: 13, color: '#1f2937', wordBreak: 'break-word' };

const actionsRow = { marginTop: 8, display: 'flex', gap: 10, flexWrap: 'wrap' };

const primaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '10px 14px',
  borderRadius: 10,
  border: '1px solid #111827',
  background: '#111827',
  color: '#fff',
  fontWeight: 700,
  fontSize: 14,
  cursor: 'pointer',
  textDecoration: 'none',
};
const secondaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '10px 14px',
  borderRadius: 10,
  border: '1px solid #e5e7eb',
  background: '#fff',
  color: '#111827',
  fontWeight: 700,
  fontSize: 14,
  cursor: 'pointer',
};

const emptyWrap = {
  ...card,
  textAlign: 'center',
  paddingTop: 36,
  paddingBottom: 36,
};
const emptyIcon = { fontSize: 32, marginBottom: 8 };
const emptyTitle = { fontWeight: 800, fontSize: 18, marginBottom: 6 };
const emptyText = { color: '#6b7280', marginBottom: 16 };

const shimmerBlock = {
  background: 'linear-gradient(90deg,#f3f4f6 25%,#eceff3 37%,#f3f4f6 63%)',
  backgroundSize: '400% 100%',
  animation: 'shimmer 1.4s ease infinite',
  borderRadius: 12,
};
const shimmerLine = {
  ...shimmerBlock,
  height: 12,
  borderRadius: 6,
};

/* ===== Responsive / overflow guard ===== */
const media = `
#wrap-detail-page { max-width: 100vw; overflow-x: hidden; }
#wrap-detail-page .grid-responsive { grid-template-columns: 1.2fr 1fr; }
@media (max-width: 960px) {
  #wrap-detail-page .grid-responsive { grid-template-columns: 1fr; }
  #wrap-detail-page h1 { font-size: 22px !important; }
  #wrap-detail-page img[alt="WrapMarket"] { height: 26px !important; }
}
@media (max-width: 600px) {
  #wrap-detail-page h1 { font-size: 20px !important; }
  #wrap-detail-page .actions-responsive a,
  #wrap-detail-page .actions-responsive button { width: 100% !important; }
}
@keyframes shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: 0 0; }
}
`;

// Inject once
const styleId = 'wrap-detail-inline-style';
if (typeof document !== 'undefined' && !document.getElementById(styleId)) {
  const tag = document.createElement('style');
  tag.id = styleId;
  tag.innerHTML = media;
  document.head.appendChild(tag);
}
